import {AppUrls} from 'shared/config/model/urls';

import React from 'react';
import {Link} from 'react-router-dom';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import IServerOutput from 'features/server/model/Server/IServer';
import useServerMetrics from 'features/server/model/useServerMetrics';
import {formatBytesToGb} from 'features/server/lib/serverMetricsHelper';
import {loadFormatter, memoryFormatter, percentFormatter} from 'features/server/lib/serverMetricsView';
import {isTranslate} from 'shared/lib/lang';
import ProgressBar from 'shared/ui/ProgressBar/ProgressBar';
import Box from 'shared/ui/Box/Box';
import Hint from 'shared/ui/Hint/Hint';
import RefreshButton from 'shared/ui/IconButton/RefreshButton';

import ServerMetricsChart from './ServerMetricsChart/ServerMetricsChart';
import ServerMetricsRangePicker from './ServerMetricsRangePicker/ServerMetricsRangePicker';
import ServerUptime from './ServerUptime/ServerUptime';

import styles from './ServerMetrics.module.scss';

interface IServerMetricsProps {
    server: IServerOutput,
}

const ServerMetrics: React.FC<IServerMetricsProps> = ({server}) => {
    const {
        tzString, range, activePreset, rangeOptions, handlePresetClick, handleCustomRangeChange, handleRefresh,
        isFetching, isLoading, mounts, summary, series, cpuData, laData, memData, fsDataByMount,
        tickFormatter, tooltipLabelFormatter,
    } = useServerMetrics(server);

    const lastMetrics = server.last_metrics;

    return (
        <Box className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.sectionTitle}>{t`Метрики`}</h3>
                <RefreshButton
                    title={t`Обновить`}
                    onClick={handleRefresh}
                    isRefreshing={isFetching}
                />
            </div>

            {
                lastMetrics && summary && (
                    <div className={styles.summary}>
                        <div className={styles.summaryItem}>
                            <ProgressBar
                                title={t`CPU`}
                                percent={summary.cpuPercent}
                            />
                        </div>
                        <div className={styles.summaryItem}>
                            <ProgressBar
                                title={
                                    <span className={styles.summaryTitle}>
                                        {t`RAM`}
                                        <span className={styles.summaryTotal}>
                                            {formatBytesToGb(summary.ramTotal)}
                                        </span>
                                    </span>
                                }
                                percent={summary.ramPercent}
                            />
                        </div>
                        <div className={styles.summaryItem}>
                            <ProgressBar
                                title={
                                    <span className={styles.summaryTitle}>
                                        {t`SWAP`}
                                        <span className={styles.summaryTotal}>
                                            {formatBytesToGb(summary.swapTotal)}
                                        </span>
                                    </span>
                                }
                                percent={summary.swapPercent}
                            />
                        </div>
                        {
                            summary.fs.map((fs) => (
                                <div className={styles.summaryItem} key={fs.mount}>
                                    <ProgressBar
                                        title={`${t`Диск`} ${fs.mount}`}
                                        percent={fs.percent}
                                    />
                                </div>
                            ))
                        }
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>{t`Load Average`}</span>
                            <span className={styles.summaryValue}>
                                {summary.la.map(loadFormatter).join(' / ')}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>{t`Uptime`}</span>
                            <span className={cn(styles.summaryValue, styles.summaryValueNoWrap)}>
                                <ServerUptime
                                    uptimeSeconds={lastMetrics.uptime_seconds}
                                    metricsTs={lastMetrics.ts}
                                    isLive={server.status === 'online'}
                                />
                            </span>
                        </div>
                    </div>
                )
            }

            {
                !lastMetrics && summary && (
                    <div className={styles.noData}>
                        {t`Сервер ещё не присылал данные`}
                    </div>
                )
            }

            <div className={styles.chartsToolbar}>
                <ServerMetricsRangePicker
                    from={range.from}
                    to={range.to}
                    retentionDays={server.retention_days}
                    tzString={tzString}
                    onChange={handleCustomRangeChange}
                />
                <div className={styles.rangeSeg}>
                    {rangeOptions.map((option) => {
                        const button = (
                            <button
                                key={option.key}
                                type="button"
                                disabled={option.disabled}
                                className={cn(styles.rangeButton, {
                                    [styles.rangeButtonActive]: activePreset === option.key,
                                    [styles.rangeButtonDisabled]: option.disabled,
                                })}
                                onClick={() => handlePresetClick(option.key)}
                            >
                                {option.label}
                            </button>
                        );

                        if (!option.disabled) {
                            return button;
                        }

                        return (
                            <Hint key={option.key} trigger={<span className={styles.rangeButtonHint}>{button}</span>}>
                                <>
                                    {t`Доступно на тарифе с хранением истории от ${option.requiredDays} дн.`}
                                    {' '}
                                    {t`(на вашем — ${server.retention_days} дн.)`}
                                    {' '}
                                    <Link to={isTranslate() ? AppUrls.PRICING : AppUrls.TARIFFS}>
                                        {t`Сменить тариф`}
                                    </Link>
                                </>
                            </Hint>
                        );
                    })}
                </div>
            </div>

            <div className={styles.grid}>
                <ServerMetricsChart
                    title={t`CPU`}
                    data={cpuData}
                    isLoading={isLoading}
                    tickFormatter={tickFormatter}
                    tooltipLabelFormatter={tooltipLabelFormatter}
                    valueFormatter={percentFormatter}
                    yDomain={[0, 100]}
                    series={series.cpu}
                />
                <ServerMetricsChart
                    title={t`Load Average`}
                    data={laData}
                    isLoading={isLoading}
                    tickFormatter={tickFormatter}
                    tooltipLabelFormatter={tooltipLabelFormatter}
                    valueFormatter={loadFormatter}
                    series={series.la}
                />
                <ServerMetricsChart
                    title={t`Память`}
                    data={memData}
                    isLoading={isLoading}
                    tickFormatter={tickFormatter}
                    tooltipLabelFormatter={tooltipLabelFormatter}
                    valueFormatter={memoryFormatter}
                    yDomain={[0, 100]}
                    series={series.mem}
                />
                {
                    (mounts ?? []).map((item) => (
                        <ServerMetricsChart
                            key={item.mount}
                            title={t`Диск ${item.mount}`}
                            data={fsDataByMount[item.mount] ?? []}
                            isLoading={isLoading}
                            tickFormatter={tickFormatter}
                            tooltipLabelFormatter={tooltipLabelFormatter}
                            valueFormatter={percentFormatter}
                            yDomain={[0, 100]}
                            series={series.fs}
                        />
                    ))
                }
            </div>
        </Box>
    );
};

export default ServerMetrics;
